import { memo, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  FlagProvider as UnleashProvider,
  UnleashClient,
  InMemoryStorageProvider,
} from '@unleash/proxy-client-react';
import type {
  IConfig,
  IToggle,
  IMutableContext,
} from '@unleash/proxy-client-react';
import {
  UNLEASH_PROXY_URL_LOCAL,
  UNLEASH_PROXY_URL_DEV,
  UNLEASH_PROXY_URL_STAGING,
  UNLEASH_PROXY_URL_PROD,
} from '@/constants/env';
import { EvaluateContext } from '../components/EvaluateContext';

const getUnleashProxyUrl = (env: typeof SWS_ENV = 'local'): string => {
  if (env === 'prod') {
    return UNLEASH_PROXY_URL_PROD;
  }
  if (env === 'staging') {
    return UNLEASH_PROXY_URL_STAGING;
  }
  if (env === 'dev') {
    return UNLEASH_PROXY_URL_DEV;
  }
  if (env === 'local') {
    return UNLEASH_PROXY_URL_LOCAL;
  }
  throw new Error(`Error: Url unhandled for environment: ${env}`);
};

type Props = {
  children: ReactNode;
  flags?: Array<IToggle>;
};

const EvaluateContextMemo = memo(EvaluateContext);

const ANALYTICS_EVENT_DATA = { environment: SWS_ENV };

export const FlagsProvider = ({ children, flags = [] }: Props) => {
  const url = useMemo(() => getUnleashProxyUrl(SWS_ENV), []);
  const [prevContext, setPrevContext] = useState<IMutableContext>({});

  const [client] = useState(() => {
    const config = {
      url,
      clientKey: 'sws-unleash',
      appName: 'mono-web',
      refreshInterval: 3600, // 1 hour
    } as IConfig;

    if (RUNTIME_ENV === 'server') {
      config.fetch = require('node-fetch');
      config.storageProvider = new InMemoryStorageProvider();
      config.bootstrap = flags;
      config.appName = `${config.appName}-ssr`;
      config.disableRefresh = true;
      config.disableMetrics = true;
    }

    return new UnleashClient(config);
  });

  return (
    <UnleashProvider unleashClient={client} startClient={false}>
      <EvaluateContextMemo
        prevContext={prevContext}
        onContextUpdate={setPrevContext}
        analyticsEventData={ANALYTICS_EVENT_DATA}
      />
      {children}
    </UnleashProvider>
  );
};
